// src/components/orders/OrderRelocationModal.tsx
// Пересадка заказа: мероприятие -> город -> зал (выбор ровно N свободных мест) -> подтверждение.
// Номер заказа и номер счёта не меняются, сумма заказа остаётся прежней —
// всё это считает бэкенд (POST /orders/{id}/relocate/preview и /relocate).
import React, { useCallback, useEffect, useMemo, useState } from "react";
import Button from "../ui/Button";
import Input from "../ui/Input";
import {
    HallScheme,
    SchemeSector,
    SchemeSeat,
    SchemeStandingPlace,
    SchemeTextLabel,
    SchemeShape,
    ScaleValue,
} from "../hall-schemes/HallSchemeEditorCanvas";
import HallPricesCanvas from "../hall-prices/HallPricesCanvas";
import {
    getHallSchemeElementsRequest,
    getHallSchemeSectionsRequest,
} from "../../services/api/hallSchemes.api";
import {
    getHallPriceCategoriesRequest,
    getHallSeatPricesRequest,
} from "../../services/api/hallPrices.api";
import {
    HallPriceCategoryItem,
    HallSeatPriceItem,
} from "../../services/types/hallPrices.types";
import { getToursRequest, getTourCitiesRequest } from "../../services/api/event-content.api";
import { TourCityItem, TourItem } from "../../services/types/event-content.types";
import {
    previewRelocateOrderRequest,
    relocateOrderRequest,
    RelocationPreview,
    RelocationResult,
    RelocationSeatItem,
    RelocationStandingItem,
} from "../../services/api/orders.api";
import { OrderItem } from "../../services/types/orders.types";

type Step = "tour" | "city" | "hall" | "confirm";

interface OrderRelocationModalProps {
    isOpen: boolean;
    order: OrderItem | null;
    // Сколько билетов в заказе сейчас — ровно столько мест нужно выбрать.
    ticketsCount: number;
    onClose: () => void;
    onRelocated: (result: RelocationResult) => void;
}

const money = (value: number | null | undefined) =>
    `${Number(value ?? 0).toFixed(2).replace(".", ",")} €`;

const formatDate = (value?: string | null) => {
    if (!value) return "—";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleString("ru-RU", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};

const seatLabel = (item: RelocationSeatItem) => {
    if (item.is_standing) {
        const zone = item.standing_name || "Стоячая зона";
        return item.sector_name && item.sector_name !== zone ? `${item.sector_name}, ${zone}` : zone;
    }
    return `${item.sector_name || "—"}, ряд ${item.row_name || "—"}, место ${item.seat_name || "—"}`;
};

const OrderRelocationModal: React.FC<OrderRelocationModalProps> = ({
    isOpen,
    order,
    ticketsCount,
    onClose,
    onRelocated,
}) => {
    const [step, setStep] = useState<Step>("tour");
    const [error, setError] = useState<string | null>(null);

    // --- шаг 1: мероприятия
    const [tours, setTours] = useState<TourItem[]>([]);
    const [toursLoading, setToursLoading] = useState(false);
    const [tourSearch, setTourSearch] = useState("");
    const [selectedTour, setSelectedTour] = useState<TourItem | null>(null);

    // --- шаг 2: города
    const [cities, setCities] = useState<TourCityItem[]>([]);
    const [citiesLoading, setCitiesLoading] = useState(false);
    const [selectedCity, setSelectedCity] = useState<TourCityItem | null>(null);

    // --- шаг 3: зал
    const [hallLoading, setHallLoading] = useState(false);
    const [scale, setScale] = useState<ScaleValue>(0.8);
    const [sectors, setSectors] = useState<SchemeSector[]>([]);
    const [seats, setSeats] = useState<SchemeSeat[]>([]);
    const [standingPlaces, setStandingPlaces] = useState<SchemeStandingPlace[]>([]);
    const [textLabels, setTextLabels] = useState<SchemeTextLabel[]>([]);
    const [shapes, setShapes] = useState<SchemeShape[]>([]);
    const [priceCategories, setPriceCategories] = useState<HallPriceCategoryItem[]>([]);
    const [seatPrices, setSeatPrices] = useState<HallSeatPriceItem[]>([]);
    const [selectedElementIds, setSelectedElementIds] = useState<string[]>([]);
    // Стоячие зоны (танцпол / фан-зона): id элемента схемы -> сколько билетов туда пересаживаем.
    const [standingCounts, setStandingCounts] = useState<Record<string, number>>({});
    const [activeStandingId, setActiveStandingId] = useState<string | null>(null);
    const [selectionHint, setSelectionHint] = useState<string | null>(null);

    // --- шаг 4: подтверждение
    const [preview, setPreview] = useState<RelocationPreview | null>(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // Сброс при каждом открытии.
    useEffect(() => {
        if (!isOpen) return;
        setStep("tour");
        setError(null);
        setSelectedTour(null);
        setSelectedCity(null);
        setCities([]);
        setSelectedElementIds([]);
        setStandingCounts({});
        setActiveStandingId(null);
        setSelectionHint(null);
        setPreview(null);
        setTourSearch("");
    }, [isOpen, order?.id]);

    // Esc — закрыть (кроме момента отправки).
    useEffect(() => {
        if (!isOpen) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape" && !submitting) onClose();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [isOpen, submitting, onClose]);

    // Загрузка мероприятий.
    useEffect(() => {
        if (!isOpen) return;
        let cancelled = false;
        setToursLoading(true);
        getToursRequest({ page: 1, size: 200 })
            .then((response) => {
                if (cancelled) return;
                const list: TourItem[] = Array.isArray(response)
                    ? response
                    : (response as any).items ?? (response as any).data ?? (response as any).results ?? [];
                setTours(list.filter((t: any) => !t.is_deleted));
            })
            .catch((err: any) => {
                if (!cancelled) setError(err?.message || "Не удалось загрузить мероприятия");
            })
            .finally(() => {
                if (!cancelled) setToursLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [isOpen]);

    const filteredTours = useMemo(() => {
        const q = tourSearch.trim().toLowerCase();
        if (!q) return tours;
        return tours.filter((t) =>
            [t.name_ru, t.name_de, t.name_en].some((n) => (n || "").toLowerCase().includes(q)),
        );
    }, [tours, tourSearch]);

    const openTour = async (tour: TourItem) => {
        setSelectedTour(tour);
        setSelectedCity(null);
        setError(null);
        setStep("city");
        setCitiesLoading(true);
        try {
            const list = await getTourCitiesRequest(tour.id);
            setCities(
                (list || [])
                    .filter((c) => !c.is_deleted)
                    .sort((a, b) => String(a.start_at).localeCompare(String(b.start_at))),
            );
        } catch (err: any) {
            setError(err?.message || "Не удалось загрузить города");
            setCities([]);
        } finally {
            setCitiesLoading(false);
        }
    };

    const cityDisabledReason = (city: TourCityItem): string | null => {
        // Пересадка С нашего концерта НА концерт партнёра разрешена: места резервируются
        // у партнёра при подтверждении. С партнёрского заказа пересадка закрыта ещё
        // на кнопке (OrderTicketsModal) и на бэкенде.
        if (!city.hall?.scheme?.id) return "У зала нет схемы";
        return null;
    };

    const loadHall = useCallback(async (city: TourCityItem) => {
        const scheme = city.hall?.scheme;
        if (!scheme?.id) return;
        setHallLoading(true);
        setError(null);
        setSelectedElementIds([]);
        setStandingCounts({});
        setActiveStandingId(null);
        setSelectionHint(null);
        try {
            const [sectionsResponse, elementsResponse, categories, prices] = await Promise.all([
                getHallSchemeSectionsRequest(scheme.id),
                getHallSchemeElementsRequest(scheme.id),
                getHallPriceCategoriesRequest(city.id),
                getHallSeatPricesRequest(city.id),
            ]);

            setSectors(
                (sectionsResponse ?? [])
                    .filter((item) => item.is_active !== false)
                    .map((item) => ({ id: item.id, name: item.name, color: item.color ?? "#6d7cff" })),
            );

            const nextSeats: SchemeSeat[] = [];
            const nextStanding: SchemeStandingPlace[] = [];
            const nextTexts: SchemeTextLabel[] = [];
            const nextShapes: SchemeShape[] = [];
            (elementsResponse ?? [])
                .filter((item) => item.is_active !== false && item.id)
                .forEach((item) => {
                    const id = item.id as string;
                    if (item.type === "seat") {
                        nextSeats.push({
                            id,
                            seatNumber: String(item.seat_number ?? 0),
                            rowNumber: String(item.row_number ?? 0),
                            x: item.x ?? 0,
                            y: item.y ?? 0,
                            rowType: item.row_type === "vertical" ? "vertical" : "horizontal",
                            sectorId: item.sector_id ?? null,
                            rowGroupId: item.row_group_id ?? `row-${id}`,
                        });
                    } else if (item.type === "standing-place") {
                        nextStanding.push({
                            id,
                            x: item.x ?? 0,
                            y: item.y ?? 0,
                            width: item.width ?? 0,
                            height: item.height ?? 0,
                            text: item.text ?? "",
                            sectorId: item.sector_id ?? null,
                        });
                    } else if (item.type === "text") {
                        nextTexts.push({
                            id,
                            x: item.x ?? 0,
                            y: item.y ?? 0,
                            width: item.width ?? 120,
                            height: item.height ?? 32,
                            text: item.text ?? "",
                        });
                    } else if (item.type === "shape") {
                        nextShapes.push({
                            id,
                            x: item.x ?? 0,
                            y: item.y ?? 0,
                            width: item.width ?? 120,
                            height: item.height ?? 80,
                            shapeType: (item.shape_type ?? "rectangle") as any,
                            borderRadius: Number(item.border_radius ?? 12),
                            isOval: Boolean(item.isOval),
                        });
                    }
                });

            setSeats(nextSeats);
            setStandingPlaces(nextStanding);
            setTextLabels(nextTexts);
            setShapes(nextShapes);
            setPriceCategories(categories ?? []);
            setSeatPrices(prices ?? []);
        } catch (err: any) {
            setError(err?.message || "Не удалось загрузить зал");
        } finally {
            setHallLoading(false);
        }
    }, []);

    const openCity = (city: TourCityItem) => {
        if (cityDisabledReason(city)) return;
        setSelectedCity(city);
        setStep("hall");
        loadHall(city);
    };

    // id элемента схемы -> запись цены места (HallSeatPrice) в этом городе
    const seatPriceByElementId = useMemo(() => {
        const map = new Map<string, HallSeatPriceItem>();
        seatPrices.forEach((sp) => map.set(sp.hall_scheme_element_id, sp));
        return map;
    }, [seatPrices]);

    const priceById = useMemo(() => {
        const map = new Map<string, number>();
        priceCategories.forEach((c) => map.set(c.id, Number(c.price)));
        return map;
    }, [priceCategories]);

    // Свободная вместимость стоячей зоны: сумма count по всем свободным строкам зоны
    // (вместимость может быть разбита на несколько строк после частичных продаж).
    const standingAvailable = useMemo(() => {
        const map = new Map<string, number>();
        seatPrices.forEach((sp) => {
            if (sp.status !== "active" || sp.order_id || sp.admin_reservation_id || !sp.price_id) return;
            map.set(sp.hall_scheme_element_id, (map.get(sp.hall_scheme_element_id) ?? 0) + Number(sp.count ?? 0));
        });
        return map;
    }, [seatPrices]);

    // Цена билета стоячей зоны — по любой свободной строке зоны.
    const standingPriceId = useMemo(() => {
        const map = new Map<string, string>();
        seatPrices.forEach((sp) => {
            if (sp.status === "active" && !sp.order_id && sp.price_id && !map.has(sp.hall_scheme_element_id)) {
                map.set(sp.hall_scheme_element_id, sp.price_id);
            }
        });
        return map;
    }, [seatPrices]);

    // Свободными считаем только места со статусом active, с ценой, без заказа/брони.
    // Места ЭТОГО заказа тоже можно выбрать (оставить человека на своём месте).
    // Стоячие зоны доступны, если в них есть свободная вместимость.
    const blockedElementIds = useMemo(() => {
        const blocked = new Set<string>();
        seats.forEach((seat) => {
            const sp = seatPriceByElementId.get(seat.id);
            if (!sp || !sp.price_id) {
                blocked.add(seat.id);
                return;
            }
            if (order && sp.order_id === order.id) return;
            const occupied =
                sp.status !== "active" || !!sp.order_id || !!sp.admin_reservation_id;
            if (occupied) blocked.add(seat.id);
        });
        standingPlaces.forEach((place) => {
            if ((standingAvailable.get(place.id) ?? 0) <= 0) blocked.add(place.id);
        });
        return blocked;
    }, [seats, standingPlaces, seatPriceByElementId, standingAvailable, order]);

    const standingTotal = useMemo(
        () => Object.values(standingCounts).reduce((sum, n) => sum + (Number(n) || 0), 0),
        [standingCounts],
    );

    const handleSelectionChange = (ids: string[]) => {
        const allowed = ids.filter((id) => !blockedElementIds.has(id) && seats.some((s) => s.id === id));
        if (allowed.length + standingTotal > ticketsCount) {
            setSelectionHint(
                `Можно выбрать только ${ticketsCount} билет(а) — столько же, сколько в заказе (места и стоячие зоны вместе). Снимите выбор с лишних.`,
            );
            return;
        }
        setSelectionHint(null);
        setSelectedElementIds(allowed);
    };

    const selectedInfo = useMemo(() => {
        const sectorName = (id: string | null) => sectors.find((s) => s.id === id)?.name ?? "—";
        return selectedElementIds
            .map((elementId) => {
                const seat = seats.find((s) => s.id === elementId);
                const sp = seatPriceByElementId.get(elementId);
                if (!seat || !sp) return null;
                return {
                    elementId,
                    seatPriceId: sp.id,
                    label: `${sectorName(seat.sectorId)}, ряд ${seat.rowNumber}, место ${seat.seatNumber}`,
                    price: sp.price_id ? priceById.get(sp.price_id) ?? 0 : 0,
                };
            })
            .filter(Boolean) as { elementId: string; seatPriceId: string; label: string; price: number }[];
    }, [selectedElementIds, seats, sectors, seatPriceByElementId, priceById]);

    // Клик по стоячей зоне: добавляем её с количеством «сколько ещё не хватает» (не больше свободного).
    const handleStandingClick = (elementId: string) => {
        setActiveStandingId(elementId);
        if (standingCounts[elementId]) return;
        const available = standingAvailable.get(elementId) ?? 0;
        const remaining = ticketsCount - selectedElementIds.length - standingTotal;
        if (available <= 0) return;
        if (remaining <= 0) {
            setSelectionHint(
                `Уже выбрано ${ticketsCount} билет(а). Уменьшите количество или снимите выбор с мест, чтобы добавить стоячую зону.`,
            );
            return;
        }
        setSelectionHint(null);
        setStandingCounts((prev) => ({ ...prev, [elementId]: Math.min(available, remaining) }));
    };

    const setStandingCount = (elementId: string, value: number) => {
        const available = standingAvailable.get(elementId) ?? 0;
        const others = selectedElementIds.length + standingTotal - (standingCounts[elementId] ?? 0);
        const max = Math.max(0, Math.min(available, ticketsCount - others));
        const next = Math.max(0, Math.min(max, Math.floor(value || 0)));
        setSelectionHint(null);
        setStandingCounts((prev) => {
            const copy = { ...prev };
            if (next <= 0) delete copy[elementId];
            else copy[elementId] = next;
            return copy;
        });
    };

    const standingInfo = useMemo(() => {
        const sectorName = (id: string | null) => sectors.find((s) => s.id === id)?.name ?? "";
        return Object.entries(standingCounts)
            .map(([elementId, count]) => {
                const place = standingPlaces.find((p) => p.id === elementId);
                if (!place) return null;
                const zone = place.text || "Стоячая зона";
                const sector = sectorName(place.sectorId);
                const priceId = standingPriceId.get(elementId);
                return {
                    elementId,
                    count,
                    available: standingAvailable.get(elementId) ?? 0,
                    label: sector && sector !== zone ? `${sector}, ${zone}` : zone,
                    price: priceId ? priceById.get(priceId) ?? 0 : 0,
                };
            })
            .filter(Boolean) as { elementId: string; count: number; available: number; label: string; price: number }[];
    }, [standingCounts, standingPlaces, sectors, standingPriceId, standingAvailable, priceById]);

    const standingPayload = (): RelocationStandingItem[] =>
        selectedCity
            ? standingInfo.map((s) => ({
                  hall_scheme_element_id: s.elementId,
                  tour_city_id: selectedCity.id,
                  count: s.count,
              }))
            : [];

    const selectedCount = selectedInfo.length + standingTotal;
    const canCheckout = selectedCount === ticketsCount && ticketsCount > 0;

    const handleCheckout = async () => {
        if (!order || !canCheckout) return;
        setError(null);
        setPreviewLoading(true);
        try {
            const res = await previewRelocateOrderRequest(
                order.id,
                selectedInfo.map((s) => s.seatPriceId),
                standingPayload(),
            );
            setPreview(res);
            setStep("confirm");
        } catch (err: any) {
            setError(err?.message || "Не удалось подготовить пересадку");
        } finally {
            setPreviewLoading(false);
        }
    };

    const handleConfirm = async () => {
        if (!order || !preview) return;
        setError(null);
        setSubmitting(true);
        try {
            // Отправляем тот же выбор, что и в предпросмотре: билеты стоячих зон
            // выделяются на бэкенде заново (в предпросмотре их строки не сохраняются).
            const res = await relocateOrderRequest(
                order.id,
                selectedInfo.map((s) => s.seatPriceId),
                standingPayload(),
            );
            onRelocated(res);
        } catch (err: any) {
            setError(err?.message || "Не удалось выполнить пересадку");
            // места могли занять — перечитываем зал
            if (selectedCity) {
                setStep("hall");
                loadHall(selectedCity);
            }
        } finally {
            setSubmitting(false);
        }
    };

    if (!isOpen || !order) return null;

    const tourName = selectedTour?.name_ru || selectedTour?.name_de || "";
    const cityName = selectedCity?.city?.name_ru || selectedCity?.city?.name_de || "";
    const hallName =
        selectedCity?.hall?.internal_name || selectedCity?.hall?.name_ru || selectedCity?.hall?.name_de || "";

    const stepTitles: Record<Step, string> = {
        tour: "1. Выберите мероприятие",
        city: "2. Выберите город",
        hall: "3. Выберите места в зале",
        confirm: "4. Подтверждение пересадки",
    };

    const cityPartnerLabel = (city: TourCityItem): string | null => {
        if (city.nashe_performance_id) return "Концерт партнёра: Nashe";
        if (city.partner_seance_id) return "Концерт партнёра: Artist Production";
        return null;
    };

    const partnerName = (p: RelocationPreview): string | null =>
        p.target_partner === "nashe_de"
            ? "Nashe"
            : p.target_partner === "artist_production"
                ? "Artist Production"
                : null;

    const notificationText = (p: RelocationPreview) => {
        if (p.notification === "tickets") return "Заказ оплачен — клиенту сразу уйдут новые билеты.";
        if (p.notification === "invoice") return "Заказ не оплачен — клиенту уйдёт обновлённый счёт на оплату.";
        return "Письмо клиенту автоматически не отправляется (заказ не оплачен и не по счёту, либо агентский).";
    };

    const renderSeatTable = (items: RelocationSeatItem[], kind: "from" | "to") => (
        <table className="table relocation-table">
            <thead>
                <tr>
                    <th>#</th>
                    <th>Мероприятие / город</th>
                    <th>Место</th>
                    <th>Цена места</th>
                    {kind === "to" ? <th>Скидка / доплата</th> : <th>Скидка</th>}
                    <th>Цена билета</th>
                </tr>
            </thead>
            <tbody>
                {items.map((item, idx) => {
                    const adj = Number(item.adjustment ?? 0);
                    const fromDiscount = item.base_price - item.final_price;
                    return (
                        <tr key={`${kind}-${item.hall_seat_price_id}`}>
                            <td>{idx + 1}</td>
                            <td>
                                <div>{item.tour_name || "—"}</div>
                                <div className="table-secondary-text">
                                    {item.city_name || "—"}
                                    {item.hall_name ? ` • ${item.hall_name}` : ""} • {formatDate(item.start_at)}
                                </div>
                            </td>
                            <td>
                                {seatLabel(item)}
                                {item.is_same_seat ? (
                                    <div className="table-secondary-text">место не меняется</div>
                                ) : null}
                            </td>
                            <td>{money(item.base_price)}</td>
                            {kind === "to" ? (
                                <td className={adj > 0 ? "relocation-discount" : adj < 0 ? "relocation-surcharge" : ""}>
                                    {adj > 0 ? `скидка ${money(adj)}` : adj < 0 ? `доплата ${money(-adj)}` : "—"}
                                    {item.discount_name ? (
                                        <div className="table-secondary-text">{item.discount_name}</div>
                                    ) : null}
                                </td>
                            ) : (
                                <td>
                                    {fromDiscount > 0.004 ? money(fromDiscount) : "—"}
                                    {item.discount_name ? (
                                        <div className="table-secondary-text">{item.discount_name}</div>
                                    ) : null}
                                </td>
                            )}
                            <td>
                                <strong>{money(item.final_price)}</strong>
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );

    return (
        <div className="scheme-editor-modal relocation-mode" role="dialog" aria-modal="true" aria-label="Пересадка">
            <div className="scheme-editor-modal-content relocation-frame">
                <header className="scheme-editor-modal-header">
                    <div className="scheme-editor-header-main">
                        <p className="scheme-editor-header-eyebrow relocation-eyebrow">Режим пересадки</p>
                        <h2 className="scheme-editor-header-title">
                            Пересадка — заказ {order.order_number}
                            <span className="relocation-step-title"> · {stepTitles[step]}</span>
                        </h2>
                    </div>
                    <div className="scheme-editor-header-actions">
                        {step !== "tour" && (
                            <Button
                                type="button"
                                variant="secondary"
                                disabled={submitting}
                                onClick={() => {
                                    setError(null);
                                    if (step === "city") setStep("tour");
                                    else if (step === "hall") setStep("city");
                                    else if (step === "confirm") setStep("hall");
                                }}
                            >
                                Назад
                            </Button>
                        )}
                        <Button type="button" variant="ghost" onClick={onClose} disabled={submitting}>
                            Отменить пересадку
                        </Button>
                    </div>
                </header>

                {step === "hall" ? (
                    <div className="scheme-editor-modal-body">
                        <aside className="scheme-editor-tools">
                            <section className="scheme-editor-section">
                                <h3 className="scheme-editor-section-title">Куда пересаживаем</h3>
                                <div className="scheme-editor-info-list">
                                    <div className="scheme-editor-info-item">
                                        <span className="scheme-editor-info-label">Мероприятие</span>
                                        <span className="scheme-editor-info-value">{tourName || "—"}</span>
                                    </div>
                                    <div className="scheme-editor-info-item">
                                        <span className="scheme-editor-info-label">Город / зал</span>
                                        <span className="scheme-editor-info-value">
                                            {cityName || "—"}
                                            {hallName ? ` • ${hallName}` : ""}
                                        </span>
                                    </div>
                                    <div className="scheme-editor-info-item">
                                        <span className="scheme-editor-info-label">Дата</span>
                                        <span className="scheme-editor-info-value">
                                            {formatDate(selectedCity?.start_at)}
                                        </span>
                                    </div>
                                </div>
                            </section>

                            <section className="scheme-editor-section">
                                <h3 className="scheme-editor-section-title">
                                    Выбрано {selectedCount} из {ticketsCount}
                                </h3>
                                <p className="relocation-muted">
                                    Выберите ровно {ticketsCount} билет(а) — столько же, сколько в заказе. Можно
                                    выбирать места и стоячие зоны (танцпол): кликните по зоне и укажите количество.
                                    Серые места и зоны заняты или недоступны.
                                </p>
                                {selectionHint && <div className="form-global-error">{selectionHint}</div>}
                                {selectedInfo.length > 0 ? (
                                    <ul className="relocation-selected-list">
                                        {selectedInfo.map((s) => (
                                            <li key={s.elementId}>
                                                <span>{s.label}</span>
                                                <span>{money(s.price)}</span>
                                            </li>
                                        ))}
                                    </ul>
                                ) : null}
                                {standingInfo.length > 0 ? (
                                    <ul className="relocation-selected-list">
                                        {standingInfo.map((s) => (
                                            <li key={s.elementId} className="relocation-standing-item">
                                                <span>
                                                    {s.label}
                                                    <span className="table-secondary-text"> • свободно {s.available}</span>
                                                </span>
                                                <span className="relocation-standing-controls">
                                                    <button
                                                        type="button"
                                                        className="icon-button"
                                                        onClick={() => setStandingCount(s.elementId, s.count - 1)}
                                                        aria-label="Меньше"
                                                    >
                                                        −
                                                    </button>
                                                    <Input
                                                        type="number"
                                                        min={0}
                                                        value={String(s.count)}
                                                        onChange={(e) => setStandingCount(s.elementId, Number(e.target.value))}
                                                        style={{ width: 64, textAlign: "center" }}
                                                    />
                                                    <button
                                                        type="button"
                                                        className="icon-button"
                                                        onClick={() => setStandingCount(s.elementId, s.count + 1)}
                                                        aria-label="Больше"
                                                    >
                                                        +
                                                    </button>
                                                    <span>{money(s.price * s.count)}</span>
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                ) : null}
                                <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
                                    <Button
                                        type="button"
                                        onClick={handleCheckout}
                                        disabled={!canCheckout || previewLoading || hallLoading}
                                    >
                                        {previewLoading ? "Подготовка..." : "Оформить"}
                                    </Button>
                                    {selectedCount > 0 && (
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            onClick={() => {
                                                setSelectedElementIds([]);
                                                setStandingCounts({});
                                                setActiveStandingId(null);
                                                setSelectionHint(null);
                                            }}
                                        >
                                            Сбросить выбор
                                        </Button>
                                    )}
                                </div>
                                {error && (
                                    <div className="form-global-error" style={{ marginTop: 10 }}>
                                        {error}
                                    </div>
                                )}
                            </section>
                        </aside>

                        <section className="scheme-editor-canvas-wrap">
                            <div className="scheme-editor-canvas-toolbar">
                                <div className="scheme-editor-toolbar-spacer" />
                                <div className="scheme-editor-scale-switch">
                                    {([1, 0.8, 0.6] as ScaleValue[]).map((value) => (
                                        <button
                                            key={value}
                                            type="button"
                                            className={`scheme-editor-scale-button ${
                                                scale === value ? "scheme-editor-scale-button-active" : ""
                                            }`}
                                            onClick={() => setScale(value)}
                                        >
                                            {value}x
                                        </button>
                                    ))}
                                </div>
                            </div>
                            {hallLoading ? (
                                <div className="scheme-editor-canvas-loading">
                                    <div className="scheme-editor-canvas-loading-content">
                                        <h3 className="scheme-editor-canvas-loading-title">Загрузка зала</h3>
                                        <p className="scheme-editor-canvas-loading-text">Подождите...</p>
                                    </div>
                                </div>
                            ) : (
                                <HallPricesCanvas
                                    scale={scale}
                                    scheme={(selectedCity?.hall?.scheme as unknown as HallScheme) ?? null}
                                    sectors={sectors}
                                    seats={seats}
                                    standingPlaces={standingPlaces}
                                    selectionTool="select"
                                    textLabels={textLabels}
                                    shapes={shapes}
                                    priceCategories={priceCategories}
                                    seatPrices={seatPrices}
                                    selectedSeatIds={selectedElementIds}
                                    onSelectedSeatIdsChange={handleSelectionChange}
                                    selectedStandingPlaceId={activeStandingId}
                                    onSelectedStandingPlaceIdChange={() => {
                                        // выбор зоны обрабатывает onStandingPlaceOrderClick
                                    }}
                                    onStandingPlaceOrderClick={handleStandingClick}
                                    blockedElementIds={blockedElementIds}
                                    onSeatPricesChange={() => {
                                        // цены в режиме пересадки не меняются
                                    }}
                                />
                            )}
                        </section>
                    </div>
                ) : (
                    <div className="relocation-body">
                        {error && <div className="form-global-error" style={{ marginBottom: 12 }}>{error}</div>}

                        {step === "tour" && (
                            <>
                                <div style={{ maxWidth: 420, marginBottom: 16 }}>
                                    <Input
                                        value={tourSearch}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTourSearch(e.target.value)}
                                        placeholder="Поиск мероприятия"
                                    />
                                </div>
                                {toursLoading ? (
                                    <p>Загружаем мероприятия...</p>
                                ) : filteredTours.length === 0 ? (
                                    <p>Мероприятия не найдены.</p>
                                ) : (
                                    <div className="relocation-grid">
                                        {filteredTours.map((tour) => (
                                            <button
                                                key={tour.id}
                                                type="button"
                                                className="relocation-card"
                                                onClick={() => openTour(tour)}
                                            >
                                                <span className="relocation-card-title">
                                                    {tour.name_ru || tour.name_de || "Без названия"}
                                                </span>
                                                <span className="relocation-card-sub">{formatDate(tour.start_at)}</span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </>
                        )}

                        {step === "city" && (
                            <>
                                <h3 className="card-title" style={{ marginBottom: 12 }}>{tourName}</h3>
                                {citiesLoading ? (
                                    <p>Загружаем города...</p>
                                ) : cities.length === 0 ? (
                                    <p>У мероприятия нет городов.</p>
                                ) : (
                                    <div className="relocation-grid">
                                        {cities.map((city) => {
                                            const reason = cityDisabledReason(city);
                                            return (
                                                <button
                                                    key={city.id}
                                                    type="button"
                                                    className={`relocation-card ${reason ? "relocation-card-disabled" : ""}`}
                                                    onClick={() => openCity(city)}
                                                    disabled={!!reason}
                                                    title={reason || undefined}
                                                >
                                                    <span className="relocation-card-title">
                                                        {city.city?.name_ru || city.city?.name_de || "—"}
                                                    </span>
                                                    <span className="relocation-card-sub">
                                                        {city.hall?.internal_name || city.hall?.name_ru || "Зал не указан"}
                                                    </span>
                                                    <span className="relocation-card-sub">{formatDate(city.start_at)}</span>
                                                    {!reason && cityPartnerLabel(city) ? (
                                                        <span className="relocation-card-sub">{cityPartnerLabel(city)}</span>
                                                    ) : null}
                                                    {reason ? <span className="relocation-card-warn">{reason}</span> : null}
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                            </>
                        )}

                        {step === "confirm" && preview && (
                            <div className="relocation-confirm">
                                <div className="relocation-question">
                                    Вы действительно хотите пересадить клиента по заказу {preview.order_number}
                                    {preview.invoice_number ? ` (счёт ${preview.invoice_number})` : ""}? Номер заказа и
                                    номер счёта не изменятся, штрихкоды перейдут на новые места.
                                </div>

                                <h4 className="relocation-section-title">Откуда</h4>
                                {renderSeatTable(preview.from_items, "from")}

                                <h4 className="relocation-section-title">Куда</h4>
                                {renderSeatTable(preview.to_items, "to")}

                                <div className="relocation-totals">
                                    <div>
                                        <span>Стоимость мест до / после</span>
                                        <strong>
                                            {money(preview.subtotal_before)} → {money(preview.subtotal_after)}
                                        </strong>
                                    </div>
                                    <div>
                                        <span>Скидка до / после</span>
                                        <strong>
                                            {money(preview.discount_before)} →{" "}
                                            {preview.discount_after < 0
                                                ? `доплата ${money(-preview.discount_after)}`
                                                : money(preview.discount_after)}
                                        </strong>
                                    </div>
                                    <div>
                                        <span>Сумма билетов</span>
                                        <strong>{money(preview.tickets_total)} (не меняется)</strong>
                                    </div>
                                    <div>
                                        <span>Сумма заказа / счёта</span>
                                        <strong>{money(preview.order_total)} (не меняется)</strong>
                                    </div>
                                </div>

                                {partnerName(preview) ? (
                                    <p className="relocation-card-warn">
                                        Пересадка на концерт партнёра ({partnerName(preview)}): места будут
                                        зарезервированы у партнёра
                                        {preview.is_paid ? " и сразу выкуплены — штрихкоды билетов выдаст партнёр" : ""}.
                                        Обратно на наш концерт этот заказ пересадить будет нельзя.
                                    </p>
                                ) : null}
                                <p className="relocation-muted">{notificationText(preview)}</p>

                                <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
                                    <Button type="button" onClick={handleConfirm} disabled={submitting}>
                                        {submitting ? "Пересаживаем..." : "Да, пересадить"}
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="secondary"
                                        onClick={() => setStep("hall")}
                                        disabled={submitting}
                                    >
                                        Изменить места
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default OrderRelocationModal;
